<template>
  <div class="with-side-panel">
    <div class="content-i">
      <div class="content-box">
        <div class="element-wrapper">
          <h6 class="element-header">
            {{ $t('dashboard.dashboard_for') + piniaUserStore().currentUser?.user?.userAccessRightsTranslation }}
          </h6>

          <Menu />

          <div class="element-box mt-4">
            <div class="row mt-4">
              <CoachingButton
                  :enabled="companySettings?.privateCoachingEnabled"
                  route-name="privateCoachingNewAndActiveList"
                  coaching-type="private"
                  label="Einzelcoaching"/>
              <CoachingButton
                  :enabled="companySettings?.groupCoachingEnabled"
                  route-name="groupCoachingNewAndActiveList"
                  coaching-type="group"
                  label="Gruppenmaßnahmen"/>
              <CoachingButton
                  :enabled="companySettings?.socioTherapyCoachingEnabled"
                  route-name="socioTherapyCoachingNewAndActiveList"
                  coaching-type="socioTherapy"
                  label="Soziotherapie"/>
              <CoachingButton
                  :enabled="companySettings?.assistedLivingCoachingEnabled"
                  route-name="assistedLivingCoachingNewAndActiveList"
                  coaching-type="assistedLiving"
                  label="Betreutes Wohnen"/>
              <CoachingButton
                  :enabled="companySettings?.caseManagementCoachingEnabled"
                  route-name="caseManagementCoachingNewAndActiveList"
                  coaching-type="caseManagement"
                  label="DRV Fallmanagement"/>
              <CoachingButton
                  :enabled="companySettings?.privateLearningCoachingEnabled"
                  route-name="privateLearningCoachingNewAndActiveList"
                  coaching-type="privateLearning"
                  label="Private Weiterbildung"/>
            </div>
          </div>

          <div class="element-box mt-4 d-flex px-3 py-4">
            <div class="col-lg-6 col-md-4 col-sm-12 px-0">
              <SelectVue
                  v-model="filterData.locationId"
                  :options="dropdownLocations"
                  classFormGroup="mb-0"
                  :placeholder="$t('general.location')"
                  :is-clear="true"
              />
            </div>
            <div class="col-auto text-center">
              <button class="btn btn-primary custom_search_button2" @click="getAllDashboardData()"
                      :disabled="!isLoaded">
                      <span v-if="isSearching">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('general.button.search_in_progress') }}
                      </span>
                <span v-else>
                        {{ $t('general.button.search') }}
                      </span>
              </button>
            </div>
          </div>

          <h6 class="mt-5">{{ $t('dashboard.customer_information') }}</h6>
          <div class="row mt-3">
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="$t('general.active_customers')"
                        :value="infoData?.countActiveCoaching?.total"/>
            </div>
            <div class="col-lg-3 col-md-6 col-sm-12">
              <CardInfo :label="$t('general.new_customers')"
                        :value="infoData?.countNewCoaching?.total"/>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6>Geleistete Einheiten im Monat</h6>
            <div class="row mt-4">
              <div class="col-sm-12">
                <div class="row">
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <SelectVue
                        v-model="filterData.engagementType"
                        :placeholder="$t('general.access_right')"
                        :options="userEngagementTypes || []"
                        :is-clear="true"/>
                  </div>

                  <div class="col-auto text-center">
                    <button class="btn btn-primary custom_search_button2" @click="getChartBar(true)"
                            :disabled="!isLoaded">
                      <span v-if="isSearching">
                        <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        {{ $t('general.button.search_in_progress') }}
                      </span>
                      <span v-else>
                        {{ $t('general.button.search') }}
                      </span>
                    </button>
                  </div>
                </div>
                <VueApexCharts width="100%" type="bar" :options="chartOptionsBar" :series="seriesSignBar"/>
              </div>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6>Übersicht gestartete Coachings</h6>
            <div class="mt-4">
              <VueApexCharts
                  width="100%"
                  type="area"
                  :options="chartOptionsArea"
                  :series="seriesStartedCoachingArea"/>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6>Übersicht beendete Coachings</h6>
            <div class="mt-4">
              <VueApexCharts
                  width="100%"
                  type="area"
                  :options="chartOptionsArea"
                  :series="seriesOverviewCompletedCoachingArea"/>
            </div>
          </div>

          <div class="element-box mb-3 mt-4">
            <h6>Durschnittliche Abschlussrate %</h6>
            <div class="mt-4">
              <VueApexCharts
                  width="100%"
                  type="area"
                  :options="chartOptionsArea"
                  :series="seriesAverageCompletionRateArea"/>
            </div>
          </div>
        </div>
      </div>
      <FormSidebar>
        <template #top>
          <h6 class="element-header">
            Sidebar
          </h6>
        </template>

        <div class="element-box pie">
          <h6>{{ $t('general.active_customers') }}</h6>
          <VueApexCharts width="100%" type="donut" :options="chartOptions" :series="seriesActiveCoach"/>
        </div>

        <div class="element-box pie">
          <h6>{{ $t('general.new_customers') }}</h6>
          <VueApexCharts :options="chartOptionsNewCoach" :series="seriesNewCoach"/>
        </div>
      </FormSidebar>
    </div>
  </div>
</template>

<script setup>
import dashboardSiteManagerService from '@/api/Dashboard/SiteManager';
import CardInfo from '@/components/card/CardInfo.vue';
import CoachingButton from '@/components/button/CoachingButton.vue';
import FormSidebar from '@/components/FormSidebar.vue';
import SelectVue from '@/components/SelectVue.vue';
import { ErrorToast } from '@/components/VueToast';
import { getOfficeByUserRoleDropdown } from '@/shared/dropdowns';
import { optionsChartArea, optionsChartBar, optionsChartDonut } from '@/shared/utils/apex';
import { piniaCompanySettingsStore } from '@/store/piniaCompanySettingsStore';
import { piniaEnumsStore } from '@/store/piniaEnumsStore';
import { piniaUserStore } from '@/store/piniaUserStore';
import Menu from '@/views/App/Dashboard/SiteManager/Menu.vue';
import { computed, onMounted, ref } from 'vue';
import VueApexCharts from 'vue3-apexcharts';

const piniaEnumsState         = piniaEnumsStore();
const { userEngagementTypes } = computed(() => piniaEnumsState.enums).value;

const piniaCompanySettingsState = piniaCompanySettingsStore();
const companySettings = computed(() => piniaCompanySettingsState.companySettings);

const chartOptions         = ref(optionsChartDonut);
const chartOptionsNewCoach = ref(optionsChartDonut);

const seriesActiveCoach = ref([]);
const seriesNewCoach    = ref([]);

const seriesSignBar                       = ref([]);
const seriesStartedCoachingArea           = ref([]);
const seriesOverviewCompletedCoachingArea = ref([]);
const seriesAverageCompletionRateArea     = ref([]);

const chartOptionsBar  = ref(optionsChartBar);
const chartOptionsArea = ref(optionsChartArea);

const dropdownLocations = ref([]);

const filterData = ref({});
const infoData   = ref({});

const isSearching = ref(false);
const isLoaded    = ref(false);

async function getInfoData() {
  try {
    const { data } = await dashboardSiteManagerService.getInfoData(filterData.value);
    infoData.value = data.data;

    seriesActiveCoach.value = Object.values(data.data.countActiveCoaching?.counts);
    seriesNewCoach.value    = Object.values(data.data.countNewCoaching?.counts);
    chartOptions.value      = {
      ...chartOptions.value,
      labels: [
        `Einzelcoaching ${data.data.countActiveCoaching.counts?.privateCoaching}`,
        `Gruppenmaßnahmen ${data.data.countActiveCoaching.counts?.groupCoaching}`,
        `Soziotherapie ${data.data.countActiveCoaching.counts?.socioTherapyCoaching}`,
        `Private Weiterbildung ${data.data.countActiveCoaching.counts?.privateLearningCoaching}`,
        `Betreutes Wohnen ${data.data.countActiveCoaching.counts?.assistedLivingCoaching}`,
        `DRV Fallmanagement ${data.data.countActiveCoaching.counts?.caseManagementCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptions.value.plotOptions.pie.donut,
            labels: {
              ...chartOptions.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptions.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.countActiveCoaching?.total.toString(),
              },
            },
          },
        },
      },
    };

    chartOptionsNewCoach.value = {
      ...chartOptionsNewCoach.value,
      labels: [
        `Einzelcoaching ${data.data.countNewCoaching.counts?.privateCoaching}`,
        `Gruppenmaßnahmen ${data.data.countNewCoaching.counts?.groupCoaching}`,
        `Soziotherapie ${data.data.countNewCoaching.counts?.socioTherapyCoaching}`,
        `Private Weiterbildung ${data.data.countNewCoaching.counts?.privateLearningCoaching}`,
        `Betreutes Wohnen ${data.data.countNewCoaching.counts?.assistedLivingCoaching}`,
        `DRV Fallmanagement ${data.data.countNewCoaching.counts?.caseManagementCoaching}`,
      ],
      plotOptions: {
        pie: {
          donut: {
            ...chartOptionsNewCoach.value.plotOptions.pie.donut,
            labels: {
              ...chartOptionsNewCoach.value.plotOptions.pie.donut.labels,
              total: {
                ...chartOptionsNewCoach.value.plotOptions.pie.donut.labels.total,
                formatter: () => data.data.countNewCoaching?.total.toString(),
              },
            },
          },
        },
      },
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getChartBar() {
  try {
    isSearching.value = true;

    const { data }        = await dashboardSiteManagerService.getUnitsDoneChart(filterData.value);
    chartOptionsBar.value = {
      ...optionsChartBar,
      xaxis: {
        categories: data.data.unitsDoneChartData.map(item => item.monthName),
      },
      colors: [
        'rgba(67, 173, 78, 0.6)',
        'rgba(75, 192, 192, 0.6)',
      ],
      plotOptions: {
        bar: {
          distributed: false,
        },
      },
    };

    chartOptionsArea.value = {
      ...optionsChartArea,
      xaxis: {
        categories: data.data.averageCompletionRate.map(item => item.monthName),
      },
      colors: [
        'rgba(67, 173, 78, 0.6)',
      ],
    };

    seriesStartedCoachingArea.value = [ {
      name: 'gestartete Coachings',
      data: data.data.startedCoachingChartData.map(item => item.total),
    } ];

    seriesOverviewCompletedCoachingArea.value = [ {
      name: 'beendete Coachings',
      data: data.data.finishedCoachingChartData.map(item => item.total),
    } ];

    seriesAverageCompletionRateArea.value = [ {
      name: 'Durschnittliche Abschlussrate %',
      data: data.data.averageCompletionRate.map(item => item.total),
    } ];

    seriesSignBar.value = [
      { name: 'unterschriebene Einheiten', data: data.data.unitsDoneChartData.map(item => item.signedUnits) },
      { name: 'Einheiten ohne Unterschrift', data: data.data.unitsDoneChartData.map(item => item.unsignedUnits) },
    ];

    isSearching.value = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }
}

async function getAllDashboardData() {
  try {
    isSearching.value = true;
    await getInfoData();
    await getChartBar();
    isSearching.value = false;
  } catch (error) {
    isSearching.value = false;
    await ErrorToast(error);
  }

}

onMounted(async () => {
  await Promise.all([
    getInfoData(),
    getChartBar(),
    getOfficeByUserRoleDropdown(dropdownLocations),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>
::v-deep(.pie .apexcharts-legend) {
  display: block !important;
}
</style>